import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Alert } from '../../../shared/components/Alert';
import { Skeleton } from '../../../shared/components/Skeleton';
import { NegociacaoPropostaTimeline } from '../components/NegociacaoPropostaTimeline';
import { StatusBadge } from './PropostasComerciaisPage';
import { useProposta } from '../hooks';

/**
 * Página standalone de negociação (rota `/propostas/negociacao/:id`).
 * Consumidor primário: link direto de notificação (`proposal.counterproposal_received`
 * / `proposal.negotiation_event`). Reusa o componente `NegociacaoPropostaTimeline`.
 */
export default function NegociacaoPropostaPage(): React.ReactElement {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: proposta, isLoading, isError } = useProposta(id);

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (isError || !proposta) return <Alert variant="error">Proposta não encontrada.</Alert>;
  const currentVersion = proposta.versions?.find((v) => v.id === proposta.currentVersionId);
  if (!currentVersion) return <Alert variant="error">Versão vigente indefinida.</Alert>;

  const readonly =
    currentVersion.status !== 'enviada_ao_cliente' && currentVersion.status !== 'em_negociacao';

  return (
    <div className="p-4 sm:p-6">
      <button
        type="button"
        onClick={() => navigate(`/propostas/${id}`)}
        className="mb-4 inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar para o detalhe
      </button>

      <div className="mb-6 flex items-center gap-3">
        <h1 className="text-xl font-semibold text-gray-900">
          Negociação — {proposta.codigo} v{currentVersion.versionNumber}
        </h1>
        <StatusBadge status={currentVersion.status} />
      </div>

      <NegociacaoPropostaTimeline
        proposalId={proposta.id}
        versionId={currentVersion.id}
        readonly={readonly}
      />
    </div>
  );
}

export { NegociacaoPropostaPage };
